import {Component, OnDestroy, OnInit} from '@angular/core';
import {AuthService} from '../../_services/auth.service';
import {DebugLogService, IDebugRequestLog} from '../../_services/debug-log.service';
import {Observable, Subscription, timer} from 'rxjs';

interface IKeyValue {
  key: string;
  value: string;
}

// Плавающая кнопка-дебагер (видна на всех страницах поверх всего, см.
// app.component.html) — по кнопке "как будто не авторизован после перехода
// с мобилки" бэктрейсить нечем: на реальном устройстве нет devtools, а
// проблема именно в том, что реально доехало до фронта (кука SSO/user_data
// от вебвью-моста) vs что было отправлено на бэк. Показывает СЫРЫЕ значения
// (не true/false) и живой список всех HTTP-запросов из DebugLogService.
@Component({
  selector: 'app-debug-panel',
  templateUrl: './debug-panel.component.html',
  styleUrls: ['./debug-panel.component.css']
})
export class DebugPanelComponent implements OnInit, OnDestroy {
  isOpen = false;
  activeTab: 'auth' | 'requests' | 'storage' = 'auth';
  expandedRequestIds = new Set<number>();

  ssoToken: string | undefined;
  isLoggedIn = false;
  isDeviceMobile = false;
  lang: string | undefined;
  rawUserDataCookie: string | undefined;
  rawUserLocalStorage: string | null = null;
  parsedUser: any = null;
  allCookies: IKeyValue[] = [];
  allLocalStorage: IKeyValue[] = [];
  currentUrl = '';
  userAgent = '';

  logs$: Observable<IDebugRequestLog[]> = this.debugLogService.logs$;

  private refreshSub: Subscription;

  constructor(
    public authService: AuthService,
    private debugLogService: DebugLogService
  ) {}

  ngOnInit(): void {
    this.refreshSnapshot();
    // Пока панель открыта, куки/сторадж могут поменяться асинхронно (мост из
    // приложения дописывает их не в момент навигации) — обновляем снапшот
    // раз в секунду, а не только по клику "Обновить".
    this.refreshSub = timer(0, 1000).subscribe(() => {
      if (this.isOpen) {
        this.refreshSnapshot();
      }
    });
  }

  ngOnDestroy(): void {
    this.refreshSub?.unsubscribe();
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.refreshSnapshot();
    }
  }

  close(): void {
    this.isOpen = false;
  }

  setTab(tab: 'auth' | 'requests' | 'storage'): void {
    this.activeTab = tab;
  }

  refreshSnapshot(): void {
    this.ssoToken = this.authService.getCookie('SSO');
    this.isLoggedIn = this.authService.isLoggedIn;
    this.isDeviceMobile = this.authService.isDeviceMobile;
    this.lang = this.authService.lang;
    this.rawUserDataCookie = this.authService.getCookie('user_data');
    this.rawUserLocalStorage = localStorage.getItem('user');
    try {
      this.parsedUser = this.authService.user;
    } catch {
      this.parsedUser = null;
    }
    this.allCookies = this.parseAllCookies();
    this.allLocalStorage = this.parseAllLocalStorage();
    this.currentUrl = window.location.href;
    this.userAgent = navigator.userAgent;
  }

  toggleRequest(id: number): void {
    if (this.expandedRequestIds.has(id)) {
      this.expandedRequestIds.delete(id);
    } else {
      this.expandedRequestIds.add(id);
    }
  }

  isExpanded(id: number): boolean {
    return this.expandedRequestIds.has(id);
  }

  clearLogs(): void {
    this.debugLogService.clear();
  }

  get logsSnapshot(): IDebugRequestLog[] {
    return this.debugLogService.snapshot;
  }

  pretty(value: any): string {
    if (value === undefined) {
      return 'undefined';
    }
    if (value === null) {
      return 'null';
    }
    if (typeof value === 'string') {
      return value;
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  async copyAll(logs: IDebugRequestLog[]): Promise<void> {
    const payload = {
      currentUrl: this.currentUrl,
      userAgent: this.userAgent,
      auth: {
        isLoggedIn: this.isLoggedIn,
        isDeviceMobile: this.isDeviceMobile,
        lang: this.lang,
        ssoToken: this.ssoToken,
        rawUserDataCookie: this.rawUserDataCookie,
        rawUserLocalStorage: this.rawUserLocalStorage,
        parsedUser: this.parsedUser
      },
      cookies: this.allCookies,
      localStorage: this.allLocalStorage,
      requests: logs
    };
    const text = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      alert('Скопировано в буфер обмена');
    } catch {
      // На части мобильных вебвью Clipboard API недоступен без явного жеста/разрешения
      window.prompt('Скопируйте вручную:', text);
    }
  }

  // В списке запросов домен одинаковый у всех и только съедает место —
  // показываем путь + query. Относительные URL (assets/i18n/...) — как есть.
  shortUrl(url: string): string {
    return (url || '').replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]+/i, '') || '/';
  }

  statusClass(log: IDebugRequestLog): string {
    if (log.pending) {
      return 'status-pending';
    }
    if (log.error || (log.status && log.status >= 400)) {
      return 'status-error';
    }
    return 'status-ok';
  }

  private parseAllCookies(): IKeyValue[] {
    return document.cookie
      .split(';')
      .map(pair => pair.trim())
      .filter(Boolean)
      .map(pair => {
        const idx = pair.indexOf('=');
        const key = idx >= 0 ? pair.slice(0, idx) : pair;
        const value = idx >= 0 ? decodeURIComponent(pair.slice(idx + 1)) : '';
        return {key, value};
      });
  }

  private parseAllLocalStorage(): IKeyValue[] {
    const result: IKeyValue[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null) {
        result.push({key, value: localStorage.getItem(key) || ''});
      }
    }
    return result;
  }
}
